import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  BellIcon,
  ArrowPathIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  EyeIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { CalendarIcon as CalendarIconSolid, BellIcon as BellIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const CalendarDashboard = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day, agenda
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(['all']);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Simulate fetching calendar events
    const fetchEvents = () => {
      const mockEvents = [
        {
          id: 'EVT001',
          title: 'Morning Ward Rounds',
          description: 'Daily patient consultation rounds in the medical ward',
          startTime: '2025-01-23T08:00:00Z',
          endTime: '2025-01-23T10:00:00Z',
          type: 'medical',
          category: 'patient_care',
          location: 'Medical Ward - Floor 3',
          attendees: [
            { name: 'Dr. Sarah Johnson', role: 'Attending Physician' },
            { name: 'Dr. Mike Chen', role: 'Resident' },
            { name: 'Nurse Emily Davis', role: 'Head Nurse' }
          ],
          priority: 'high',
          status: 'confirmed',
          recurring: 'daily',
          reminder: '15min',
          color: '#ef4444'
        },
        {
          id: 'EVT002',
          title: 'Staff Meeting - Department Heads',
          description: 'Monthly department heads meeting to discuss operations and updates',
          startTime: '2025-01-23T14:00:00Z',
          endTime: '2025-01-23T15:30:00Z',
          type: 'meeting',
          category: 'administrative',
          location: 'Conference Room A',
          attendees: [
            { name: 'Dr. Robert Wilson', role: 'Medical Director' },
            { name: 'Jennifer Wu', role: 'HR Manager' },
            { name: 'Lisa Rodriguez', role: 'Operations Manager' }
          ],
          priority: 'medium',
          status: 'confirmed',
          recurring: 'monthly',
          reminder: '30min',
          color: '#3b82f6'
        },
        {
          id: 'EVT003',
          title: 'Emergency Response Training',
          description: 'Mandatory training session for emergency response protocols',
          startTime: '2025-01-24T10:00:00Z',
          endTime: '2025-01-24T12:00:00Z',
          type: 'training',
          category: 'education',
          location: 'Training Center - Room 101',
          attendees: [
            { name: 'All Medical Staff', role: 'Participants' }
          ],
          priority: 'high',
          status: 'confirmed',
          recurring: 'quarterly',
          reminder: '1hour',
          color: '#f59e0b'
        },
        {
          id: 'EVT004',
          title: 'Patient Consultation - John Smith',
          description: 'Follow-up consultation for post-operative care',
          startTime: '2025-01-24T15:30:00Z',
          endTime: '2025-01-24T16:00:00Z',
          type: 'appointment',
          category: 'patient_care',
          location: 'Consultation Room 5',
          attendees: [
            { name: 'Dr. Sarah Johnson', role: 'Physician' },
            { name: 'John Smith', role: 'Patient' }
          ],
          priority: 'medium',
          status: 'confirmed',
          recurring: 'none',
          reminder: '15min',
          color: '#10b981'
        },
        {
          id: 'EVT005',
          title: 'Equipment Maintenance - MRI Unit',
          description: 'Scheduled maintenance for MRI machine #3',
          startTime: '2025-01-25T06:00:00Z',
          endTime: '2025-01-25T08:00:00Z',
          type: 'maintenance',
          category: 'technical',
          location: 'Radiology Department',
          attendees: [
            { name: 'Technical Team', role: 'Maintenance Staff' }
          ],
          priority: 'high',
          status: 'confirmed',
          recurring: 'monthly',
          reminder: '1day',
          color: '#8b5cf6'
        },
        {
          id: 'EVT006',
          title: 'Board Meeting - Strategic Planning',
          description: 'Quarterly board meeting to discuss strategic initiatives and budget',
          startTime: '2025-01-26T09:00:00Z',
          endTime: '2025-01-26T12:00:00Z',
          type: 'meeting',
          category: 'administrative',
          location: 'Executive Conference Room',
          attendees: [
            { name: 'Board Members', role: 'Directors' },
            { name: 'Executive Team', role: 'Leadership' }
          ],
          priority: 'high',
          status: 'confirmed',
          recurring: 'quarterly',
          reminder: '1day',
          color: '#dc2626'
        },
        {
          id: 'EVT007',
          title: 'Research Seminar - Clinical Trials',
          description: 'Weekly research seminar on ongoing clinical trials and findings',
          startTime: '2025-01-27T16:00:00Z',
          endTime: '2025-01-27T17:30:00Z',
          type: 'seminar',
          category: 'education',
          location: 'Auditorium',
          attendees: [
            { name: 'Research Team', role: 'Researchers' },
            { name: 'Medical Staff', role: 'Attendees' }
          ],
          priority: 'medium',
          status: 'tentative',
          recurring: 'weekly',
          reminder: '2hours',
          color: '#06b6d4'
        }
      ];

      setEvents(mockEvents);
      setLoading(false);
    };

    setTimeout(fetchEvents, 500);
  }, []);

  const categories = [
    { id: 'all', name: 'All Events', color: '#6b7280' },
    { id: 'patient_care', name: 'Patient Care', color: '#ef4444' },
    { id: 'administrative', name: 'Administrative', color: '#3b82f6' },
    { id: 'education', name: 'Education & Training', color: '#f59e0b' },
    { id: 'technical', name: 'Technical & Maintenance', color: '#8b5cf6' }
  ];

  const viewModes = [
    { id: 'month', name: 'Month', icon: CalendarDaysIcon },
    { id: 'week', name: 'Week', icon: ViewColumnsIcon },
    { id: 'day', name: 'Day', icon: Squares2X2Icon },
    { id: 'agenda', name: 'Agenda', icon: ListBulletIcon }
  ];

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    switch (viewMode) {
      case 'month':
        newDate.setMonth(currentDate.getMonth() + direction);
        break;
      case 'week':
        newDate.setDate(currentDate.getDate() + (direction * 7));
        break;
      case 'day':
        newDate.setDate(currentDate.getDate() + direction);
        break;
      default:
        newDate.setMonth(currentDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDateHeader = () => {
    const options = {
      year: 'numeric',
      month: 'long',
      ...(viewMode === 'day' && { day: 'numeric' })
    };
    return currentDate.toLocaleDateString('en-US', options);
  };

  const formatEventTime = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${start.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })} - ${end.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.startTime);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategories.includes('all') || selectedCategories.includes(event.category);
    
    return matchesSearch && matchesCategory;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'tentative':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayNamesShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    return (
      <div className="bg-white rounded-lg sm:rounded-xl shadow overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {dayNames.map((day, index) => (
            <div key={day} className="p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-gray-700">
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{dayNamesShort[index]}</span>
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const dayEvents = day ? getEventsForDate(day) : [];
            const isToday = day && day.toDateString() === new Date().toDateString();
            
            return (
              <div
                key={index}
                className={`min-h-[80px] sm:min-h-[120px] md:min-h-[140px] border-r border-b border-gray-200 p-1 sm:p-2 ${
                  !day ? 'bg-gray-50' : 'bg-white hover:bg-gray-50 transition-colors'
                }`}
              >
                {day && (
                  <>
                    <div className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 text-xs sm:text-sm mb-1 sm:mb-2 ${
                      isToday 
                        ? 'bg-blue-600 text-white rounded-full font-semibold' 
                        : 'text-gray-900'
                    }`}>
                      {day.getDate()}
                    </div>
                    <div className="space-y-0.5 sm:space-y-1">
                      {dayEvents.slice(0, window.innerWidth < 640 ? 2 : 3).map(event => (
                        <div
                          key={event.id}
                          onClick={() => handleEventClick(event)}
                          className="text-[10px] sm:text-xs p-0.5 sm:p-1 rounded cursor-pointer truncate transition-colors"
                          style={{ backgroundColor: event.color + '20', color: event.color }}
                          title={event.title}
                        >
                          <span className="hidden sm:inline">{event.title}</span>
                          <span className="sm:hidden">•</span>
                        </div>
                      ))}
                      {dayEvents.length > (window.innerWidth < 640 ? 2 : 3) && (
                        <div className="text-[10px] sm:text-xs text-gray-500 font-medium">
                          +{dayEvents.length - (window.innerWidth < 640 ? 2 : 3)} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAgendaView = () => {
    const sortedEvents = [...filteredEvents].sort((a, b) => 
      new Date(a.startTime) - new Date(b.startTime)
    );

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {sortedEvents.length === 0 ? (
            <div className="p-12 text-center">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No events found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : (
            sortedEvents.map(event => (
              <div key={event.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div 
                      className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: event.color }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                            {event.title}
                          </h3>
                          <p className="mt-1 text-sm text-gray-600 line-clamp-2 sm:line-clamp-none">
                            {event.description}
                          </p>
                        </div>
                        <div className="mt-2 sm:mt-0 sm:ml-4 flex flex-wrap sm:flex-col items-start space-x-2 sm:space-x-0 sm:space-y-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(event.priority)}`}>
                            {event.priority.toUpperCase()}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(event.status)}`}>
                            {event.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">{formatEventTime(event.startTime, event.endTime)}</span>
                        </div>
                        <div className="flex items-center">
                          <MapPinIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </div>
                        <div className="flex items-center">
                          <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">{event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}</span>
                        </div>
                        {event.reminder && (
                          <div className="flex items-center">
                            <BellIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                            <span className="truncate">{event.reminder} reminder</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 sm:ml-4">
                    <button
                      onClick={() => handleEventClick(event)}
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors rounded"
                      title="View details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors rounded"
                      title="Edit event"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 transition-colors rounded"
                      title="More options"
                    >
                      <EllipsisHorizontalIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Calendar Content */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center">
                <CalendarIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Calendar</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Manage your schedule and appointments
                  </p>
                </div>
              </div>
              <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                <Link
                  to="/calendar/schedule"
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Schedule Event
                </Link>
                <Link
                  to="/calendar/rooms"
                  className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <MapPinIcon className="h-4 w-4 mr-2" />
                  Meeting Rooms
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Controls */}
        <div className="bg-white shadow rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            {/* Navigation */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center bg-gray-100 rounded-lg">
                <button
                  onClick={() => navigateDate(-1)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-l-lg transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <div className="px-3 sm:px-4 py-2 text-sm sm:text-base font-medium text-gray-900 min-w-[180px] sm:min-w-[200px] text-center">
                  {formatDateHeader()}
                </div>
                <button
                  onClick={() => navigateDate(1)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-r-lg transition-colors"
                >
                  <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
              
              <button
                onClick={goToToday}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Today
              </button>
            </div>

            {/* View Mode Selector */}
            <div className="flex items-center space-x-1 overflow-x-auto">
              <div className="flex items-center space-x-1 flex-shrink-0">
                {viewModes.map(mode => (
                  <button
                    key={mode.id}
                    onClick={() => setViewMode(mode.id)}
                    className={`${
                      viewMode === mode.id
                        ? 'bg-blue-100 text-blue-700 border-blue-200'
                        : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                    } inline-flex items-center px-2 sm:px-3 py-2 border text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap`}
                  >
                    <mode.icon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{mode.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
              <div className="relative flex-1 sm:flex-none">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full sm:w-auto pl-9 sm:pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Filters</span>
                <span className="sm:hidden">Filter</span>
              </button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categories
                  </label>
                  <div className="space-y-2">
                    {categories.map(category => (
                      <label key={category.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category.id)}
                          onChange={(e) => {
                            if (category.id === 'all') {
                              setSelectedCategories(e.target.checked ? ['all'] : []);
                            } else {
                              const newCategories = e.target.checked
                                ? [...selectedCategories.filter(c => c !== 'all'), category.id]
                                : selectedCategories.filter(c => c !== category.id);
                              setSelectedCategories(newCategories.length ? newCategories : ['all']);
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: category.color }}
                          ></div>
                          {category.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSelectedCategories(['all']);
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Calendar View */}
        {viewMode === 'month' ? renderMonthView() : renderAgendaView()}

        {/* Quick Stats */}
        <div className="mt-4 sm:mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <div className="bg-white rounded-lg sm:rounded-xl shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Today's Events</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                  {getEventsForDate(new Date()).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">This Week</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                  {events.filter(event => {
                    const eventDate = new Date(event.startTime);
                    const now = new Date();
                    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
                    const weekEnd = new Date(weekStart);
                    weekEnd.setDate(weekStart.getDate() + 6);
                    return eventDate >= weekStart && eventDate <= weekEnd;
                  }).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
                <BellIconSolid className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Pending</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                  {events.filter(event => event.status === 'tentative').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                <ArrowPathIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Recurring</p>
                <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                  {events.filter(event => event.recurring !== 'none').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-8 sm:top-20 mx-auto border w-full max-w-lg sm:max-w-2xl lg:max-w-3xl shadow-lg rounded-lg sm:rounded-xl bg-white">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 pr-4">
                  {selectedEvent.title}
                </h3>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4 sm:space-y-6">
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {selectedEvent.description}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <span className="text-sm font-medium text-gray-700 block mb-1">Time:</span>
                    <p className="text-sm text-gray-600">
                      {formatEventTime(selectedEvent.startTime, selectedEvent.endTime)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700 block mb-1">Location:</span>
                    <p className="text-sm text-gray-600 truncate" title={selectedEvent.location}>
                      {selectedEvent.location}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700 block mb-1">Priority:</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(selectedEvent.priority)}`}>
                      {selectedEvent.priority.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700 block mb-1">Status:</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(selectedEvent.status)}`}>
                      {selectedEvent.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-gray-700 block mb-2">Attendees:</span>
                  <div className="max-h-32 overflow-y-auto">
                    <ul className="space-y-1 text-sm text-gray-600">
                      {selectedEvent.attendees.map((attendee, index) => (
                        <li key={index} className="flex items-center justify-between py-1">
                          <span>{attendee.name}</span>
                          <span className="text-xs text-gray-500">{attendee.role}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  onClick={() => setShowEventModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors">
                  Edit Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default CalendarDashboard;
